/**
 * EmergingMarkets.tsx — Session 3
 * ================================
 * Renders the output of the thesis scorer (Session 2). Two views:
 *
 *   1. List       — Tier 1 (5 cards), Tier 2 (10 rows), Tier 3 (30 rows)
 *   2. Detail     — Single-metro drill-in with 10-indicator breakdown
 *                    (5 undervaluation + 5 momentum), each labeled with its
 *                    numeric value or "N/A" when the scorer dropped it.
 *
 * Backend endpoints (auth required, admin):
 *   GET  /searcher/emerging            → { computed_at, tiers: { "1":[…], "2":[…], "3":[…] } }
 *   GET  /searcher/emerging/{cbsa}     → per-metro detail
 *   POST /searcher/score-thesis        → kicks off background recompute
 *
 * Design system: Fraunces display / EB Garamond body / JetBrains mono for
 * numbers. Same color tokens as Searcher.tsx.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

// ── Design tokens (locked — match Searcher.tsx / Dashboard.css) ──────────────
const bg       = '#ffffff';
const surface  = '#fafaf8';
const gold     = '#b8956a';
const goldDim  = 'rgba(184,149,106,0.15)';
const border   = '#e6ddd4';
const primary  = '#0d3d5c';
const secondary= '#12526e';
const muted    = '#7a8a8a';
const white    = '#ffffff';
const green    = '#1A7A4A';
const red      = '#CC2200';

const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";
const fMono = "'JetBrains Mono', monospace";

// ── Types ────────────────────────────────────────────────────────────────────
interface MarketRow {
  cbsa_code: string;
  name: string;
  population: number | null;
  state_codes: string | null;
  score_undervaluation: number | null;
  score_momentum: number | null;
  score_thesis: number | null;
  tier: number;
  tier_rank: number;
  trend_direction: string | null;
  trend_strength: number | null;
}

interface TierResponse {
  computed_at: string | null;
  tiers: { '1': MarketRow[]; '2': MarketRow[]; '3': MarketRow[] };
  message?: string;
}

interface IndicatorRow {
  indicator: string;
  value: number | null;
  higher_is_better: boolean;
}

interface DetailResponse {
  cbsa_code: string;
  name: string;
  population: number | null;
  state_codes: string | null;
  score_undervaluation: number | null;
  score_momentum: number | null;
  score_thesis: number | null;
  tier: number | null;
  tier_rank: number | null;
  thesis_computed_at: string | null;
  trend_direction: string | null;
  trend_strength: number | null;
  undervaluation: { indicators: IndicatorRow[]; n_inputs_present: number };
  momentum:       { indicators: IndicatorRow[]; n_inputs_present: number };
}

// ── Indicator presentation — labels, units, precision ────────────────────────
// Keeps the UI decoupled from the scorer's internal variable names.
const IND_META: Record<string, { label: string; unit: 'ratio' | 'pct' | 'bps' | 'x' | 'pp'; precision: number; hint: string }> = {
  price_to_income:          { label: 'Price / Income',            unit: 'x',   precision: 2, hint: 'ACS median home value ÷ median household income. Lower = cheaper.' },
  price_to_rent:            { label: 'Price / Annual Rent',       unit: 'x',   precision: 1, hint: 'Median home value ÷ (ZORI monthly rent × 12). Lower = cheaper.' },
  hpi_gap_vs_trend:         { label: 'HPI Gap vs Trend',          unit: 'pct', precision: 1, hint: 'Deviation of latest FHFA HPI from its OLS trendline. Negative = below trend.' },
  rent_minus_hpi_growth_3y: { label: 'Rent − HPI Growth (3yr)',   unit: 'pp',  precision: 1, hint: '3yr rent CAGR minus 3yr HPI CAGR. Positive = yield widening.' },
  pudb_ust_spread:          { label: 'Mortgage − 10Y UST Spread', unit: 'pp',  precision: 2, hint: 'PUDB median multifamily agency rate minus 10-year Treasury.' },

  population_yoy:           { label: 'Population YoY',            unit: 'pct', precision: 2, hint: 'Latest ACS population growth vs prior year.' },
  employment_5yr_cagr:      { label: 'Employment 5yr CAGR',       unit: 'pct', precision: 2, hint: 'BLS LAUS employment level, trailing 5-year compound growth.' },
  net_migration_rate:       { label: 'Net Migration Rate',        unit: 'pct', precision: 2, hint: 'IRS SOI net migration ÷ population.' },
  permit_acceleration:      { label: 'Permit Acceleration',       unit: 'x',   precision: 2, hint: 'Annualized trailing-3-month permits ÷ trailing-12-month pace. >1 accelerating.' },
  rent_growth_3y_cagr:      { label: 'Rent Growth 3yr CAGR',      unit: 'pct', precision: 2, hint: 'ZORI all-homes rent index, 3-year compound growth.' },
};

function fmtValue(v: number | null, unit: string, precision: number): string {
  if (v === null || v === undefined || Number.isNaN(v)) return 'N/A';
  if (unit === 'pct') return `${v.toFixed(precision)}%`;
  if (unit === 'pp')  return `${v.toFixed(precision)} pp`;
  if (unit === 'bps') return `${(v * 100).toFixed(0)} bps`;
  if (unit === 'x')   return `${v.toFixed(precision)}×`;
  return v.toFixed(precision);
}

function fmtPop(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

function fmtScore(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(1);
}

// ── Shared style atoms ───────────────────────────────────────────────────────
const smallLabel: React.CSSProperties = {
  fontSize: 9,
  fontFamily: 'sans-serif',
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: muted,
};

const pageTitle: React.CSSProperties = {
  fontFamily: fHead,
  fontWeight: 600,
  color: primary,
  lineHeight: 1.1,
};

// ── Props ────────────────────────────────────────────────────────────────────
interface Props { token: string; }

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function EmergingMarkets({ token }: Props) {
  const headers = { Authorization: `Bearer ${token}` };

  const [tierData, setTierData]   = useState<TierResponse | null>(null);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState<string | null>(null);

  const [selected, setSelected]   = useState<string | null>(null);   // cbsa_code
  const [detail,   setDetail]     = useState<DetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState<string | null>(null);

  const [recomputing, setRecomputing] = useState(false);
  const [recomputeMsg, setRecomputeMsg] = useState<string | null>(null);

  // ── Load tier list ─────────────────────────────────────────────────────────
  const loadTiers = useCallback(() => {
    setLoading(true);
    setError(null);
    axios.get<TierResponse>(`${API}/searcher/emerging`, { headers })
      .then(r => {
        setTierData(r.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load tiers');
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadTiers(); }, [loadTiers]);

  // ── Load detail when a metro is clicked ────────────────────────────────────
  useEffect(() => {
    if (!selected) { setDetail(null); setDetailError(null); return; }
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    axios.get<DetailResponse>(`${API}/searcher/emerging/${selected}`, { headers })
      .then(r => { setDetail(r.data); setDetailLoading(false); })
      .catch(err => {
        setDetailError(err?.response?.data?.detail || err?.message || 'Failed to load detail');
        setDetailLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // ── Trigger recompute ──────────────────────────────────────────────────────
  const recompute = () => {
    if (recomputing) return;
    setRecomputing(true);
    setRecomputeMsg(null);
    axios.post(`${API}/searcher/score-thesis`, {}, { headers })
      .then(() => {
        setRecomputeMsg('Recompute started. It runs against all 239 metros and typically takes ~13 minutes. Refresh once complete.');
        setRecomputing(false);
      })
      .catch(err => {
        setRecomputeMsg(err?.response?.data?.detail || err?.message || 'Failed to trigger recompute');
        setRecomputing(false);
      });
  };

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selected) {
    return (
      <DetailView
        loading={detailLoading}
        error={detailError}
        detail={detail}
        onBack={() => setSelected(null)}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: fBody }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ ...smallLabel, color: gold, marginBottom: 10 }}>Emerging Markets · Thesis Scorer</div>
          <h1 style={{ ...pageTitle, fontSize: 36, margin: 0 }}>
            Undervaluation × Momentum
          </h1>
          <div style={{ marginTop: 8, fontSize: 14, color: secondary, maxWidth: 620, lineHeight: 1.45 }}>
            Ranks every U.S. CBSA with population ≥ 200,000 on a cheap-and-rising thesis — five price indicators multiplied by five growth indicators.
            Hot priced-in markets and cheap declining markets are both filtered out; only the intersection appears below.
          </div>
          {tierData?.computed_at && (
            <div style={{ marginTop: 12, fontSize: 11, color: muted, fontFamily: fMono }}>
              Last scored {new Date(tierData.computed_at).toLocaleString()}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={loadTiers}
            disabled={loading}
            style={{
              padding: '9px 18px', backgroundColor: 'transparent', color: secondary,
              border: `1px solid ${border}`, fontSize: 10, letterSpacing: 2,
              textTransform: 'uppercase', fontWeight: 700, cursor: loading ? 'default' : 'pointer',
              fontFamily: 'sans-serif', opacity: loading ? 0.5 : 1,
            }}>
            Refresh
          </button>
          <button
            onClick={recompute}
            disabled={recomputing}
            style={{
              padding: '9px 18px', backgroundColor: gold, color: white,
              border: `1px solid ${gold}`, fontSize: 10, letterSpacing: 2,
              textTransform: 'uppercase', fontWeight: 700, cursor: recomputing ? 'default' : 'pointer',
              fontFamily: 'sans-serif', opacity: recomputing ? 0.6 : 1,
            }}>
            {recomputing ? 'Triggering…' : 'Recompute'}
          </button>
        </div>
      </div>

      {recomputeMsg && (
        <div style={{
          padding: '12px 16px', backgroundColor: goldDim, border: `1px solid ${border}`,
          borderLeft: `3px solid ${gold}`, marginBottom: 24, fontSize: 13, color: primary,
        }}>
          {recomputeMsg}
        </div>
      )}

      {loading && <div style={{ color: muted, fontSize: 13 }}>Loading tiers…</div>}
      {error &&   <div style={{ color: red,   fontSize: 13 }}>Error: {error}</div>}

      {!loading && tierData && !error && (
        <>
          {tierData.message && (
            <div style={{
              padding: '16px 20px', backgroundColor: surface, border: `1px solid ${border}`,
              marginBottom: 24, fontSize: 13, color: secondary,
            }}>
              {tierData.message}
            </div>
          )}

          {/* TIER 1 — cards */}
          <TierSection
            title="Tier 1 — Highest Conviction"
            subtitle="Top 5 — cheapest on fundamentals AND strongest momentum"
            count={tierData.tiers['1'].length}
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {tierData.tiers['1'].map(m => (
                <MarketCard key={m.cbsa_code} m={m} onClick={() => setSelected(m.cbsa_code)} />
              ))}
            </div>
          </TierSection>

          {/* TIER 2 — rows */}
          <TierSection
            title="Tier 2 — Strong Candidates"
            subtitle="Ranks 6–15 — solid across both halves"
            count={tierData.tiers['2'].length}
          >
            <MarketTable rows={tierData.tiers['2']} onSelect={c => setSelected(c)} />
          </TierSection>

          {/* TIER 3 — rows */}
          <TierSection
            title="Tier 3 — Watch List"
            subtitle="Ranks 16–45 — monitor for entry"
            count={tierData.tiers['3'].length}
          >
            <MarketTable rows={tierData.tiers['3']} onSelect={c => setSelected(c)} />
          </TierSection>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier section wrapper
// ─────────────────────────────────────────────────────────────────────────────
function TierSection({
  title, subtitle, count, children,
}: {
  title: string; subtitle: string; count: number; children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 44 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${border}` }}>
        <h2 style={{ ...pageTitle, fontSize: 20, margin: 0 }}>{title}</h2>
        <div style={{ fontSize: 11, color: muted, letterSpacing: 1.5 }}>
          {count} markets
        </div>
      </div>
      <div style={{ fontSize: 12, color: secondary, marginBottom: 16 }}>{subtitle}</div>
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Market card (tier 1)
// ─────────────────────────────────────────────────────────────────────────────
function MarketCard({ m, onClick }: { m: MarketRow; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        backgroundColor: white,
        border: `1px solid ${hover ? gold : border}`,
        borderLeft: `3px solid ${gold}`,
        padding: '18px 20px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: hover ? '0 4px 12px rgba(13,61,92,0.10)' : '0 1px 3px rgba(13,61,92,0.06)',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ ...smallLabel, color: gold }}>Rank #{m.tier_rank}</div>
        <div style={{ ...smallLabel, color: muted, fontFamily: fMono, letterSpacing: 1 }}>{m.cbsa_code}</div>
      </div>
      <div style={{ fontFamily: fHead, fontSize: 18, fontWeight: 600, color: primary, lineHeight: 1.2, marginBottom: 4 }}>
        {m.name}
      </div>
      <div style={{ fontSize: 11, color: muted, marginBottom: 14 }}>
        Pop {fmtPop(m.population)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, paddingTop: 12, borderTop: `1px solid ${surface}` }}>
        <ScoreBlock label="Thesis"       value={m.score_thesis}         accent />
        <ScoreBlock label="Undervalued"  value={m.score_undervaluation} />
        <ScoreBlock label="Momentum"     value={m.score_momentum} />
      </div>
    </div>
  );
}

function ScoreBlock({ label, value, accent }: { label: string; value: number | null; accent?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 8, letterSpacing: 1.5, color: muted, fontFamily: 'sans-serif', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: fMono, fontSize: accent ? 18 : 14, fontWeight: accent ? 700 : 500, color: accent ? gold : primary }}>
        {fmtScore(value)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Market table (tiers 2 & 3)
// ─────────────────────────────────────────────────────────────────────────────
function MarketTable({ rows, onSelect }: { rows: MarketRow[]; onSelect: (cbsa: string) => void }) {
  if (!rows.length) return <div style={{ color: muted, fontSize: 12 }}>No markets in this tier.</div>;
  return (
    <div style={{ backgroundColor: white, border: `1px solid ${border}` }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '60px minmax(220px, 1fr) 100px 90px 90px 90px',
        padding: '10px 16px',
        backgroundColor: surface,
        borderBottom: `1px solid ${border}`,
        fontSize: 9,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        fontFamily: 'sans-serif',
        color: muted,
      }}>
        <div>Rank</div>
        <div>Market</div>
        <div style={{ textAlign: 'right' }}>Pop</div>
        <div style={{ textAlign: 'right' }}>Undervalued</div>
        <div style={{ textAlign: 'right' }}>Momentum</div>
        <div style={{ textAlign: 'right' }}>Thesis</div>
      </div>
      {rows.map((m, i) => (
        <MarketRowItem key={m.cbsa_code} m={m} isLast={i === rows.length - 1} onClick={() => onSelect(m.cbsa_code)} />
      ))}
    </div>
  );
}

function MarketRowItem({ m, isLast, onClick }: { m: MarketRow; isLast: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '60px minmax(220px, 1fr) 100px 90px 90px 90px',
        padding: '12px 16px',
        alignItems: 'center',
        cursor: 'pointer',
        backgroundColor: hover ? goldDim : white,
        borderBottom: isLast ? 'none' : `1px solid ${surface}`,
        transition: 'background-color 0.12s',
      }}
    >
      <div style={{ fontFamily: fMono, fontSize: 13, color: gold, fontWeight: 700 }}>
        #{m.tier_rank + (m.tier === 2 ? 5 : m.tier === 3 ? 15 : 0)}
      </div>
      <div>
        <div style={{ fontFamily: fHead, fontSize: 15, color: primary, fontWeight: 500 }}>{m.name}</div>
        <div style={{ fontSize: 10, color: muted, fontFamily: fMono, marginTop: 2 }}>CBSA {m.cbsa_code}</div>
      </div>
      <div style={{ textAlign: 'right', fontFamily: fMono, fontSize: 12, color: secondary }}>
        {fmtPop(m.population)}
      </div>
      <div style={{ textAlign: 'right', fontFamily: fMono, fontSize: 13, color: primary }}>
        {fmtScore(m.score_undervaluation)}
      </div>
      <div style={{ textAlign: 'right', fontFamily: fMono, fontSize: 13, color: primary }}>
        {fmtScore(m.score_momentum)}
      </div>
      <div style={{ textAlign: 'right', fontFamily: fMono, fontSize: 14, fontWeight: 700, color: gold }}>
        {fmtScore(m.score_thesis)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail view
// ─────────────────────────────────────────────────────────────────────────────
function DetailView({
  loading, error, detail, onBack,
}: {
  loading: boolean; error: string | null; detail: DetailResponse | null; onBack: () => void;
}) {
  return (
    <div style={{ fontFamily: fBody }}>
      <button
        onClick={onBack}
        style={{
          backgroundColor: 'transparent', border: 'none', color: secondary,
          fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700,
          cursor: 'pointer', padding: 0, marginBottom: 24, fontFamily: 'sans-serif',
        }}>
        ← Back to Tiers
      </button>

      {loading && <div style={{ color: muted, fontSize: 13 }}>Loading indicator breakdown…</div>}
      {error &&   <div style={{ color: red,   fontSize: 13 }}>Error: {error}</div>}

      {!loading && detail && !error && (
        <>
          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ ...smallLabel, color: gold, marginBottom: 8 }}>
              {detail.tier ? `Tier ${detail.tier} · Rank ${detail.tier_rank}` : 'Unranked'}
            </div>
            <h1 style={{ ...pageTitle, fontSize: 36, margin: 0 }}>{detail.name}</h1>
            <div style={{ marginTop: 10, fontSize: 13, color: secondary }}>
              CBSA {detail.cbsa_code} · Pop {fmtPop(detail.population)}{detail.state_codes ? ` · ${detail.state_codes}` : ''}
            </div>
          </div>

          {/* Score summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12, marginBottom: 36,
          }}>
            <SummaryCard label="Thesis Score"      value={detail.score_thesis}         accent />
            <SummaryCard label="Undervaluation"    value={detail.score_undervaluation} />
            <SummaryCard label="Momentum"          value={detail.score_momentum} />
            <SummaryCard label="Inputs (Under)"    value={detail.undervaluation.n_inputs_present} raw={`${detail.undervaluation.n_inputs_present} / 5`} />
            <SummaryCard label="Inputs (Momentum)" value={detail.momentum.n_inputs_present}       raw={`${detail.momentum.n_inputs_present} / 5`} />
          </div>

          {/* Breakdowns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 24 }}>
            <BreakdownPanel title="Undervaluation" subtitle="Cheaper = better"  rows={detail.undervaluation.indicators} />
            <BreakdownPanel title="Momentum"       subtitle="Higher = better"   rows={detail.momentum.indicators} />
          </div>

          {detail.thesis_computed_at && (
            <div style={{ marginTop: 36, paddingTop: 18, borderTop: `1px solid ${border}`, fontSize: 11, color: muted, fontFamily: fMono }}>
              Scores last computed {new Date(detail.thesis_computed_at).toLocaleString()}.
              Indicator values above are recomputed on-demand from the current database.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, raw }: { label: string; value: number | null; accent?: boolean; raw?: string }) {
  return (
    <div style={{
      backgroundColor: white, border: `1px solid ${border}`,
      borderLeft: `3px solid ${accent ? gold : border}`, padding: '14px 16px',
    }}>
      <div style={{ ...smallLabel, color: muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: fMono, fontSize: accent ? 26 : 22, fontWeight: accent ? 700 : 500, color: accent ? gold : primary, lineHeight: 1.1 }}>
        {raw !== undefined ? raw : fmtScore(value)}
      </div>
    </div>
  );
}

function BreakdownPanel({ title, subtitle, rows }: { title: string; subtitle: string; rows: IndicatorRow[] }) {
  return (
    <div style={{ backgroundColor: white, border: `1px solid ${border}` }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${border}`, backgroundColor: surface }}>
        <div style={{ fontFamily: fHead, fontSize: 16, fontWeight: 600, color: primary }}>{title}</div>
        <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>{subtitle}</div>
      </div>
      <div>
        {rows.map((r, i) => {
          const meta = IND_META[r.indicator] || { label: r.indicator, unit: 'ratio' as const, precision: 2, hint: '' };
          const missing = r.value === null || r.value === undefined;
          return (
            <div key={r.indicator} style={{
              display: 'grid', gridTemplateColumns: '1fr auto',
              gap: 12, padding: '14px 18px',
              borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${surface}`,
              alignItems: 'start',
            }}>
              <div>
                <div style={{ fontFamily: fHead, fontSize: 14, color: primary, fontWeight: 500 }}>{meta.label}</div>
                <div style={{ fontSize: 11, color: muted, lineHeight: 1.4, marginTop: 3, maxWidth: 380 }}>{meta.hint}</div>
              </div>
              <div style={{
                fontFamily: fMono, fontSize: 14, fontWeight: 600,
                color: missing ? muted : primary, whiteSpace: 'nowrap',
                paddingTop: 2,
              }}>
                {missing ? 'N/A' : fmtValue(r.value, meta.unit, meta.precision)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
